import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from '@chakra-ui/react';
import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import {
  NationalityType,
  Errors
} from '../../contexts/types';
import { CustomNumberInput } from '../InvestmentScreening/CustomNumberInput';
import { formatLocalDateTime } from '../../utils/utils';

/**
 * マスタメンテナンス - 顧客国籍設定 画面
 */
const Nationality: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.Nationality.title');
  /** エンティティ略称（Toast表示用） */
  const ENTITY = t('features.MasterMaintenance.Nationality.entityLabel');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル(1列目) */
  const fixedCol1Style = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
  }

  /** 列固定スタイル(2列目) */
  const fixedCol2Style = {
    position: "sticky" as const,
    left: "120px",
    zIndex: "1",
    boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** 国籍マスタデータ */
  const [masterData, setMasterData] = useState<NationalityType[]>([]);

  /** 編集対象国籍マスタの初期値 */
  const initialEditTarget: NationalityType = {
    nationalityId: "",
    nationalityNameJpn: "",
    nationalityNameEng: "",
    deleted: false,
    order: 0,
    createDate: "",
    updateDate: "",
  };

  /** 編集対象国籍マスタ */
  const [editTarget, setEditTarget] = useState<NationalityType>(initialEditTarget);

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用 */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）国籍ID
  const [filterNationalityId, setFilterNationalityId] = useState<string>("");
  // （フィルター）国籍名（日本語）
  const [filterNationalityNameJpn, setFilterNationalityNameJpn] = useState<string>("");

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  /** フィルター済みマスタデータ */
  const filteredMasterData = (masterData ?? []).filter(m => {
    if (!m) {
      return false;
    }

    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    return (
      hasFilterString(m.nationalityId, filterNationalityId) &&
      hasFilterString(m.nationalityNameJpn, filterNationalityNameJpn)
    );
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await fetchMasterData();
    }

    // 実行
    fetch();
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
          query {
            getNationality(hasDeleted: true) {
              nationalityId
              nationalityNameJpn
              nationalityNameEng
              deleted
              order
              createDate
              updateDate
            }
          }`
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getNationality) {
        // 削除フラグがfalseのものを先に、表示順の昇順でソート
        const sorted = [...response.data.getNationality].sort((a: NationalityType, b: NationalityType) => {
          // 削除フラグ: false を先に
          if (a.deleted !== b.deleted) {
            return a.deleted ? 1 : -1;
          }
          // 表示順の昇順
          const orderA = a.order ?? 9999;
          const orderB = b.order ?? 9999;
          return orderA - orderB;
        });
        setMasterData(sorted);
      } else {
        console.error(`Failed to call API getNationality. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `顧客国籍一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「国籍名」リンク押下
   * @param nationalityId 国籍ID
   */
  const handleEdit = async (nationalityId?: string | null) => {
    try {
      const payload = {
        query: `
          query getNationalityById($nationalityId: String) {
            getNationalityById(nationalityId: $nationalityId) {
              nationalityId
              nationalityNameJpn
              nationalityNameEng
              deleted
              order
              createDate
              updateDate
            }
          }`,
        variables: { nationalityId }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getNationalityById) {
        setEditTarget(response?.data?.getNationalityById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getNationalityById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `顧客国籍情報取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | null) => Boolean(value || null);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string) => {
      const requiredResult = required(target[propertyName]);

      if (!requiredResult) {
        localErrors[propertyName] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    if (!requiredAndSetError(editTarget, "nationalityId", t('features.MasterMaintenance.Nationality.field.id'))) {
      result = false;
    }

    if (!requiredAndSetError(editTarget, "nationalityNameJpn", t('features.MasterMaintenance.Nationality.field.nameJpn'))) {
      result = false;
    }

    if (!requiredAndSetError(editTarget, "nationalityNameEng", t('features.MasterMaintenance.Nationality.field.nameEng'))) {
      result = false;
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   */
  const putEditTarget = async () => {
    try {
      // 登録パラメータ
      const input = {
        nationalityId: editTarget.nationalityId,
        nationalityNameJpn: editTarget.nationalityNameJpn,
        nationalityNameEng: editTarget.nationalityNameEng,
        order: editTarget.order ?? 0,
        deleted: editTarget.deleted,
        updateDate: editTarget.updateDate,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putNationality($input: NationalityInput!) {
            putNationality(input: $input) {status}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result.data.putNationality.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putNationality.`);
        return true;
      } else if (result?.data?.putNationality.status === 'duplicate') {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.Nationality.message.duplicateId'),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Duplicate error. putNationality. nationalityId = ${editTarget.nationalityId}`);
        return false;
      } else if (result?.data?.putNationality.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putNationality.`);
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putNationality. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putNationality. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 一覧更新
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget(initialEditTarget);
    setErrors({});
  }

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <Box>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>
        </Box>
        <TableContainer overflowX="auto" overflowY="auto" w="1200px" h="calc(100dvh - 150px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={120} />
              <col width={200} />
              <col width={200} />
              <col width={100} />
              <col width={120} />
              <col width={230} />
              <col width={230} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedCol1Style}>
                  {t('features.MasterMaintenance.Nationality.field.id')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterNationalityId} setFilterValue={setFilterNationalityId} />
                </Th>
                <Th {...headerCellStyle} {...fixedCol2Style}>
                  {t('features.MasterMaintenance.Nationality.field.nameJpn')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterNationalityNameJpn} setFilterValue={setFilterNationalityNameJpn} />
                </Th>
                <Th {...headerCellStyle}>{t('features.MasterMaintenance.Nationality.field.nameEng')}</Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.registrationDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : "white";

                return (
                  <Tr role="group" key={record.nationalityId} _hover={{ bg: "gray.200" }}>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedCol1Style}>
                      {record.nationalityId}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedCol2Style}>
                      <Button
                        variant="link"
                        textDecoration="underline"
                        color="blue.500"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          handleEdit(record.nationalityId)
                        }}
                        sx={{
                          display: 'inline-block',
                          maxW: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {record.nationalityNameJpn}
                      </Button>
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{record.nationalityNameEng}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.order}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('features.MasterMaintenance.Nationality.modal.editTitle')}</Text>
          </ModalHeader>
          <ModalBody>
            <VStack>

              <FormControl isInvalid={errors.nationalityId?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.Nationality.field.id')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.nationalityId || ""}
                      onChange={(event) => { changeEditTarget("nationalityId", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.Nationality.placeholder.id')}
                      width={valueWidth}
                      maxLength={InputLimits.NATIONALITY_ID}
                      isInvalid={errors.nationalityId?.isError}
                      isReadOnly={!!editTarget.updateDate}
                      bg={editTarget.updateDate ? "gray.100" : "white"}
                    />
                    <FormErrorMessage mt={0}>{errors.nationalityId?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.nationalityNameJpn?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.Nationality.field.nameJpn')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.nationalityNameJpn || ""}
                      onChange={(event) => { changeEditTarget("nationalityNameJpn", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.Nationality.placeholder.nameJpn')}
                      width={valueWidth}
                      maxLength={InputLimits.NATIONALITY_NAME}
                      isInvalid={errors.nationalityNameJpn?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.nationalityNameJpn?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.nationalityNameEng?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.Nationality.field.nameEng')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.nationalityNameEng || ""}
                      onChange={(event) => { changeEditTarget("nationalityNameEng", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.Nationality.placeholder.nameEng')}
                      width={valueWidth}
                      maxLength={InputLimits.NATIONALITY_NAME}
                      isInvalid={errors.nationalityNameEng?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.nationalityNameEng?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.sortOrder')}
                  </FormLabel>
                  <VStack align="left">
                    <CustomNumberInput
                      value={editTarget.order || 0}
                      onChange={(value) => { changeEditTarget("order", value) }}
                      placeholder={t('common.label.sortOrder')}
                      min={0}
                      max={9999}
                      decimalPlaces={0}
                      w={150}
                    />
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Nationality;
